import * as React from 'react'

import { isEqual } from 'lodash'

import type { Change, ChangesByEmployee } from 'modules/employees/pages/EditInBulk/types'
import cloneChangeWithProperty from 'modules/employees/pages/EditInBulk/utils/cloneChangeWithProperty'
import mapColumKeyToChangeProperty from 'modules/employees/pages/EditInBulk/utils/mapColumKeyToChangeProperty'
import type { ColumnNames } from 'modules/employees/pages/EditInBulk/vars'

import type { TableSelection } from './useTableSelection'

/**
 * Internal structure to keep local and pushed changes in one place
 * WHY: because the save operation in the backend is
 * an asynchronous job which doesn't return the new state of the
 * employees. So we need to keep track of the changes we have pushed
 * to support the reset to last pushed operation
 *
 * If at some point in the future the backend implementation changes
 * and returns the new state we most likely won't need this structure
 * and we could just clean the local changes after every save operation
 */
interface ChangesHistory {
  local: ChangesByEmployee
  pushed: ChangesByEmployee
}

export enum ChangeActionType {
  MARK_CHANGES_AS_PUSHED = 'pushed_changes',
  RESET_TO_LAST_PUSHED = 'reset_changes',
  ADD_CHANGE_TO_EMPLOYEES_IDS = 'set_changes',
  ADD_CHANGES_TO_TABLE_SELECTION = 'from_selected_table',
}

export type ChangeAction =
  | { type: ChangeActionType.MARK_CHANGES_AS_PUSHED }
  | { type: ChangeActionType.RESET_TO_LAST_PUSHED }
  | {
      type: ChangeActionType.ADD_CHANGE_TO_EMPLOYEES_IDS
      payload: {
        employees_ids: number[]
        change: Change
      }
    }
  | {
      type: ChangeActionType.ADD_CHANGES_TO_TABLE_SELECTION
      payload: {
        table_selection: TableSelection
        change: Change
      }
    }

const calculateFromEmployeesIds = (
  prevState: ChangesByEmployee,
  employeesIds: number[],
  change: Change
): ChangesByEmployee => {
  // reduce the list of employees to be staged to a ChangesByEmployee object
  return employeesIds.reduce<ChangesByEmployee>((prevState, employeeId): ChangesByEmployee => {
    const newState = new Map(prevState)
    const prevChange = prevState.get(employeeId)

    return prevChange
      ? newState.set(employeeId, { ...prevChange, ...change })
      : newState.set(employeeId, change)
  }, prevState)
}

// important: the change will be applied in column basis
// so the field manager_id of a change will be applied only
// to the selected employees of the manager column
const calculateFromTableSelected = (
  prevState: ChangesByEmployee,
  tableSelection: TableSelection,
  change: Change
): ChangesByEmployee => {
  // this method looks complex because tableSelection has a complex structure
  // reduces the properties from tableSelection to a ChangesByEmployee object
  return Object.keys(tableSelection).reduce<ChangesByEmployee>(
    (prevState, columnKey): ChangesByEmployee => {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const columnName = columnKey as ColumnNames
      const selectedEmployeesForColumn = tableSelection[columnName]

      if (!selectedEmployeesForColumn?.length) return prevState

      const changeProperties = mapColumKeyToChangeProperty(columnName)

      return changeProperties.reduce<ChangesByEmployee>((state, property) => {
        return calculateFromEmployeesIds(
          state,
          selectedEmployeesForColumn,
          cloneChangeWithProperty(change, property)
        )
      }, prevState)
    },
    prevState
  )
}

const changeReducer = (state: ChangesHistory, action: ChangeAction): ChangesHistory => {
  switch (action.type) {
    case ChangeActionType.ADD_CHANGE_TO_EMPLOYEES_IDS:
      return {
        ...state,
        local: calculateFromEmployeesIds(
          state.local,
          action.payload.employees_ids,
          action.payload.change
        ),
      }
    case ChangeActionType.ADD_CHANGES_TO_TABLE_SELECTION:
      return {
        ...state,
        local: calculateFromTableSelected(
          state.local,
          action.payload.table_selection,
          action.payload.change
        ),
      }
    case ChangeActionType.RESET_TO_LAST_PUSHED:
      return {
        ...state,
        local: state.pushed,
      }
    case ChangeActionType.MARK_CHANGES_AS_PUSHED:
      return {
        ...state,
        pushed: state.local,
      }
    default:
      return state
  }
}

export default function useChangesForEmployees() {
  const [changesByEmployee, dispatchChangeOnEmployees] = React.useReducer(changeReducer, {
    local: new Map(),
    pushed: new Map(),
  } as ChangesHistory)

  const isLocalChangesToBePushed = !isEqual(changesByEmployee.local, changesByEmployee.pushed)
  const localChangesByEmployee = changesByEmployee.local

  return {
    isLocalChangesToBePushed,
    localChangesByEmployee,
    dispatchLocalChangeOnEmployees: dispatchChangeOnEmployees,
  }
}
